/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import { Button, Badge, Avatar, DividerNew, useFlags } from 'sarsaparilla';

import { authModalActions, logout } from '../../auth';
import { useTypedDispatch } from '../../redux';
import type { MenuAccountItemsType } from './Navbar.types';
import { useGetAvatarUrl, useUserName } from './Navbar.utils';

interface MobileMenuAccountPanelProps {
    isCommercialAccount?: boolean;
    isGuest?: boolean;
    hideBottomSignOut?: boolean;
    menuAccountItems: MenuAccountItemsType;
    isLogged?: boolean;
    isGuestAllowed?: boolean;
}

export function MobileMenuAccountPanel({
    isCommercialAccount,
    isGuest = false,
    menuAccountItems,
    hideBottomSignOut = false,
    isLogged = false,
    isGuestAllowed,
}: MobileMenuAccountPanelProps) {
    const dispatch = useTypedDispatch();
    const avatarUrl = useGetAvatarUrl();
    const userName = useUserName();
    const { newAccountNavLinks } = useFlags();
    const signOut = () => logout(dispatch);
    const signIn = () => {
        return isGuestAllowed
            ? dispatch(authModalActions.openLoginWithGuestModal())
            : dispatch(authModalActions.openLoginModal());
    };

    const getCurrentUrl = () => window.location.href;

    const renderItems = () =>
        menuAccountItems.map((item, index) => {
            if (item.shouldHide || (!isLogged && item.title === 'Availability Alerts')) {
                return null;
            }

            return (
                <li className={item.parentClasses} key={index}>
                    <Button
                        isUnstyled
                        clickTagCategory={item.clickTagCategory}
                        clickTagAction={item.clickTagAction}
                        clickTagLabel={getCurrentUrl()}
                        className="rec-select-option-button"
                        title={item.title}
                        aria-label={item.ariaLabel}
                        href={item.href}
                    >
                        {item.text}
                        {item.badge > 0 && (
                            <span className="rec-select-option-badge-wrapper">
                                <Badge appearance="info" max={10}>
                                    {item.badge}
                                </Badge>
                            </span>
                        )}
                    </Button>
                </li>
            );
        });

    return (
        <div className={cx('nav-mobile-menu-account-panel', { 'is-guest': isGuest })}>
            {isLogged ? (
                <div>
                    <div className="nav-mobile-user-profile-head">
                        <Button
                            isUnstyled
                            href={newAccountNavLinks ? '/account' : '/account/profile'}
                            className="username-button"
                        >
                            <div className="nav-profile-info">
                                <Avatar
                                    src={avatarUrl}
                                    alt={userName}
                                    size="md"
                                    className="data-hj-suppress dark-avatar"
                                />
                                <span className="ml-1 data-hj-suppress">{userName}</span>
                            </div>
                        </Button>
                        <Button
                            appearance="link"
                            className="rec-close-dropdown-button"
                            onClick={signOut}
                            aria-label="Sign Out"
                            type="button"
                        >
                            Sign Out
                        </Button>
                    </div>
                    <div className="nav-user-profile-divider-wrapper">
                        <DividerNew />
                    </div>
                </div>
            ) : (
                <div className="sign-in-button mb-1">
                    <Button
                        shouldFitContainer
                        appearance="tertiary-white"
                        type="button"
                        onClick={signIn}
                    >
                        Sign Up / Log In
                    </Button>
                </div>
            )}
            <ul>
                {!isGuest && renderItems()}

                {isCommercialAccount ? (
                    <li className="rec-select-option">
                        <Button
                            isUnstyled
                            clickTagCategory="Global Navigation"
                            clickTagAction="Commercial Account"
                            clickTagLabel={getCurrentUrl()}
                            className="rec-select-option-button"
                            href="/account/facilities"
                        >
                            Commercial Account
                        </Button>
                    </li>
                ) : null}

                {hideBottomSignOut ? null : (
                    <li className="rec-select-option">
                        <button
                            className="rec-close-dropdown-button"
                            onClick={signOut}
                            type="button"
                            id="ga-global-nav-account-sign-out-link"
                        >
                            Sign Out
                        </button>
                    </li>
                )}
            </ul>
        </div>
    );
}
